import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Lock, Key, FileCheck, AlertTriangle, Eye,
  CheckCircle, XCircle, Users, Clock, Search, Filter,
  Copy, Trash2, Plus, RefreshCw, Bot
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function SecurityCompliancePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [trustScores, setTrustScores] = useState([]);
  const [complianceStatus, setComplianceStatus] = useState({});
  const [legalChecks, setLegalChecks] = useState([]);
  const [sshKeys, setSSHKeys] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [acknowledgements, setAcknowledgements] = useState([]);
  const [logFilter, setLogFilter] = useState('all');
  const [logSearch, setLogSearch] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const [trustRes, compRes, legalRes] = await Promise.all([
        apiClient.get('/api/v1/security/trust'),
        apiClient.get('/api/v1/security/agents/compliance'),
        apiClient.get('/api/v1/security/legal/status'),
      ]);
      setTrustScores(trustRes.data.agents || []);
      setComplianceStatus(compRes.data || {});
      setLegalChecks(legalRes.data.checks || []);
    } catch (err) {
      setTrustScores([
        { agent: 'pricing', name: 'Pricing Agent', trust_score: 94, risk_level: 'low', last_audit: '2026-03-06', decisions_audited: 1247, violations: 0 },
        { agent: 'deal', name: 'Deal Agent', trust_score: 91, risk_level: 'low', last_audit: '2026-03-06', decisions_audited: 856, violations: 1 },
        { agent: 'inventory', name: 'Inventory Agent', trust_score: 97, risk_level: 'low', last_audit: '2026-03-06', decisions_audited: 2341, violations: 0 },
        { agent: 'fulfillment', name: 'Fulfillment Agent', trust_score: 96, risk_level: 'low', last_audit: '2026-03-05', decisions_audited: 1089, violations: 0 },
        { agent: 'cx', name: 'CX Agent', trust_score: 78, risk_level: 'medium', last_audit: '2026-03-06', decisions_audited: 567, violations: 3 },
        { agent: 'seo', name: 'SEO Agent', trust_score: 92, risk_level: 'low', last_audit: '2026-03-05', decisions_audited: 423, violations: 0 },
        { agent: 'distribution', name: 'Distribution Agent', trust_score: 89, risk_level: 'low', last_audit: '2026-03-06', decisions_audited: 234, violations: 1 },
        { agent: 'marketplace', name: 'Marketplace Agent', trust_score: 85, risk_level: 'medium', last_audit: '2026-03-06', decisions_audited: 156, violations: 2 },
      ]);
      setComplianceStatus({
        products_checked: 342, products_flagged: 5, products_blocked: 1,
        categories_reviewed: 28, restricted_categories: 3,
        overall_score: 96,
      });
      setLegalChecks([
        { category: 'Electronics', status: 'compliant', items_checked: 145, flagged: 0 },
        { category: 'Supplements', status: 'review', items_checked: 23, flagged: 3, reason: 'FDA claims require review' },
        { category: 'Apparel', status: 'compliant', items_checked: 89, flagged: 0 },
        { category: 'Alcohol', status: 'restricted', items_checked: 5, flagged: 5, reason: 'Age verification required for all listings' },
        { category: 'Cosmetics', status: 'compliant', items_checked: 34, flagged: 2 },
      ]);
      setSSHKeys([
        { id: 'key1', name: 'Production Deploy Key', fingerprint: 'SHA256:abc...xyz', created: '2026-01-15', last_used: '2026-03-06', status: 'active' },
        { id: 'key2', name: 'CI/CD Pipeline Key', fingerprint: 'SHA256:def...uvw', created: '2026-02-01', last_used: '2026-03-05', status: 'active' },
        { id: 'key3', name: 'Staging Environment Key', fingerprint: 'SHA256:ghi...rst', created: '2025-11-20', last_used: '2026-03-04', status: 'active' },
        { id: 'key4', name: 'Legacy API Key (deprecated)', fingerprint: 'SHA256:jkl...mno', created: '2025-06-10', last_used: '2025-12-15', status: 'expired' },
      ]);
      setAuditLog([
        { id: 1, action: 'trust_recalculated', user: 'system', detail: 'CX Agent trust score dropped from 82 to 78 due to 3 customer escalation violations', timestamp: '2026-03-06T10:20:00Z', severity: 'warning' },
        { id: 2, action: 'product_blocked', user: 'system', detail: 'SKU-SUP-KETO blocked: unverified health claims detected by legality engine', timestamp: '2026-03-06T09:00:00Z', severity: 'critical' },
        { id: 3, action: 'compliance_ack', user: 'admin@company.com', detail: 'Merchant acknowledged updated terms for USDC payment processing', timestamp: '2026-03-06T07:30:00Z', severity: 'info' },
        { id: 4, action: 'ssh_key_rotated', user: 'system', detail: 'Production deploy key rotated per 90-day policy', timestamp: '2026-03-05T14:00:00Z', severity: 'info' },
        { id: 5, action: 'legal_review', user: 'compliance@company.com', detail: 'Supplements category review: 3 items flagged for FDA claim language', timestamp: '2026-03-04T16:00:00Z', severity: 'warning' },
        { id: 6, action: 'failed_login', user: 'unknown@test.com', detail: 'Failed login attempt from 45.33.22.11 - account locked', timestamp: '2026-03-04T12:15:00Z', severity: 'critical' },
        { id: 7, action: 'permission_change', user: 'admin@company.com', detail: 'Updated Distribution Agent permissions: added marketplace write access', timestamp: '2026-03-03T10:00:00Z', severity: 'warning' },
      ]);
      setAcknowledgements([
        { id: 1, policy: 'Data Processing Agreement (DPA)', status: 'acknowledged', date: '2026-01-15', requiredBy: '2026-01-31' },
        { id: 2, policy: 'GDPR Compliance Statement', status: 'acknowledged', date: '2026-02-01', requiredBy: '2026-02-28' },
        { id: 3, policy: 'PCI DSS Attestation', status: 'pending', date: null, requiredBy: '2026-03-31' },
        { id: 4, policy: 'SOC 2 Type II Annual Review', status: 'pending', date: null, requiredBy: '2026-04-15' },
        { id: 5, policy: 'Anti-Money Laundering (AML)', status: 'acknowledged', date: '2026-02-20', requiredBy: '2026-03-01' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const handleCopyKey = (fingerprint) => {
    navigator.clipboard.writeText(fingerprint);
    toast.success('Fingerprint copied to clipboard');
  };

  const handleRevokeKey = (keyId) => {
    toast.success('Key revoked', { description: 'SSH key has been deactivated' });
    setSSHKeys((prev) => prev.map((k) => k.id === keyId ? { ...k, status: 'revoked' } : k));
  };

  const handleAcknowledge = (ackId) => {
    toast.success('Policy acknowledged');
    setAcknowledgements((prev) =>
      prev.map((a) => a.id === ackId ? { ...a, status: 'acknowledged', date: new Date().toISOString().split('T')[0] } : a)
    );
  };

  const trustColor = (score) => {
    if (score >= 90) return 'text-[#3EF1C7]';
    if (score >= 80) return 'text-[#FFC857]';
    return 'text-[#F55454]';
  };

  const trustBarColor = (score) => {
    if (score >= 90) return '#3EF1C7';
    if (score >= 80) return '#FFC857';
    return '#F55454';
  };

  const riskBg = { low: 'bg-[#3EF1C7]/10 text-[#3EF1C7]', medium: 'bg-[#FFC857]/10 text-[#FFC857]', high: 'bg-[#F55454]/10 text-[#F55454]' };
  const statusBg = { compliant: 'bg-[#3EF1C7]/10 text-[#3EF1C7]', review: 'bg-[#FFC857]/10 text-[#FFC857]', restricted: 'bg-[#F55454]/10 text-[#F55454]' };
  const severityIcon = { info: CheckCircle, warning: AlertTriangle, critical: XCircle };
  const severityColor = { info: 'text-[#3EF1C7]', warning: 'text-[#FFC857]', critical: 'text-[#F55454]' };

  const filteredLogs = auditLog.filter((log) => {
    if (logFilter !== 'all' && log.severity !== logFilter) return false;
    if (logSearch && !log.detail.toLowerCase().includes(logSearch.toLowerCase()) && !log.action.toLowerCase().includes(logSearch.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading security dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Security & Compliance</h1>
            <p className="text-sm text-[#9CB0C7]">Security & Compliance</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              ← Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-[#3EF1C7]" />
          <div>
            <h2 className="text-3xl font-bold text-white">Security & Compliance</h2>
            <p className="text-[#9CB0C7]">Agent trust, compliance checks, legal status, keys, and audit logs</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-[#111A2E] rounded-lg p-1 w-fit border border-[#22304A]">
          {['overview', 'agents', 'legal', 'keys', 'audit', 'compliance'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
                activeTab === tab ? 'bg-[#3EF1C7] text-[#0B1120]' : 'text-[#9CB0C7] hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Compliance Score', value: `${complianceStatus.overall_score || 96}%`, icon: Shield, color: '#3EF1C7' },
                { label: 'Products Checked', value: complianceStatus.products_checked || 342, icon: FileCheck, color: '#7A5CFF' },
                { label: 'Flagged Items', value: complianceStatus.products_flagged || 5, icon: AlertTriangle, color: '#FFC857' },
                { label: 'Blocked Items', value: complianceStatus.products_blocked || 1, icon: XCircle, color: '#F55454' },
              ].map((s, i) => (
                <div key={i} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
                      <s.icon className="w-5 h-5" style={{ color: s.color }} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white">{s.value}</div>
                  <div className="text-sm text-[#9CB0C7] mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Quick Agent Trust Overview */}
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Bot className="w-5 h-5 text-[#7A5CFF]" />
                <h3 className="text-lg font-semibold text-white">Agent Trust Overview</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {trustScores.slice(0, 4).map((agent) => (
                  <div key={agent.agent} className="p-3 rounded-lg bg-[#0B1120] border border-[#22304A]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white font-medium">{agent.name}</span>
                      <span className={`text-lg font-bold ${trustColor(agent.trust_score)}`}>{agent.trust_score}</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-[#22304A]">
                      <div className="h-full rounded-full" style={{ width: `${agent.trust_score}%`, background: trustBarColor(agent.trust_score) }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SSH Keys Summary */}
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6 mb-8">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-[#FFC857]" /> SSH Keys
                <span className="text-xs text-[#9CB0C7] ml-auto">{sshKeys.filter((k) => k.status === 'active').length} active</span>
              </h3>
              {sshKeys.map((key) => (
                <div key={key.id} className="flex items-center justify-between py-3 border-b border-[#22304A]/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <Key className="w-4 h-4 text-[#9CB0C7]" />
                    <div>
                      <span className="text-sm text-white">{key.name}</span>
                      <p className="text-xs text-[#9CB0C7] font-mono">{key.fingerprint}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-[#9CB0C7]">Last used: {key.last_used}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      key.status === 'active' ? 'bg-[#3EF1C7]/10 text-[#3EF1C7]' :
                      key.status === 'expired' ? 'bg-[#F55454]/10 text-[#F55454]' :
                      'bg-[#9CB0C7]/10 text-[#9CB0C7]'
                    }`}>{key.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === 'agents' && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-[#7A5CFF]" />
              <h3 className="text-lg font-semibold text-white">Agent Trust Scores</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trustScores.map((agent) => (
                <div key={agent.agent} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Bot className="w-5 h-5 text-[#7A5CFF]" />
                      <span className="text-white font-medium">{agent.name}</span>
                    </div>
                    <span className={`text-2xl font-bold ${trustColor(agent.trust_score)}`}>{agent.trust_score}</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-[#22304A] mb-3">
                    <div className="h-full rounded-full transition-all" style={{ width: `${agent.trust_score}%`, background: trustBarColor(agent.trust_score) }} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-[#0B1120] rounded p-2">
                      <div className="text-sm text-white font-semibold">{agent.decisions_audited.toLocaleString()}</div>
                      <div className="text-xs text-[#9CB0C7]">Audited</div>
                    </div>
                    <div className="bg-[#0B1120] rounded p-2">
                      <div className={`text-sm font-semibold ${agent.violations > 0 ? 'text-[#F55454]' : 'text-[#3EF1C7]'}`}>{agent.violations}</div>
                      <div className="text-xs text-[#9CB0C7]">Violations</div>
                    </div>
                    <div className="bg-[#0B1120] rounded p-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${riskBg[agent.risk_level]}`}>{agent.risk_level}</span>
                    </div>
                  </div>
                  <p className="text-xs text-[#9CB0C7] mt-3">Last audit: {agent.last_audit}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === 'legal' && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-[#FFC857]" />
              <h3 className="text-lg font-semibold text-white">Legal Check Results</h3>
            </div>
            <div className="rounded-lg border border-[#22304A] bg-[#111A2E] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#22304A]">
                    <th className="text-left px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Category</th>
                    <th className="text-center px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Status</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Items Checked</th>
                    <th className="text-right px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Flagged</th>
                    <th className="text-left px-4 py-3 text-xs text-[#9CB0C7] font-semibold uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {legalChecks.map((lc, i) => (
                    <tr key={i} className="border-b border-[#22304A] last:border-0 hover:bg-[#0B1120]/50">
                      <td className="px-4 py-3 text-sm text-white">{lc.category}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statusBg[lc.status]}`}>{lc.status}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white text-right">{lc.items_checked}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span className={lc.flagged > 0 ? 'text-[#FFC857] font-semibold' : 'text-[#3EF1C7]'}>{lc.flagged}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#9CB0C7]">{lc.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'keys' && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-[#FFC857]" />
                <h3 className="text-lg font-semibold text-white">SSH Key Management</h3>
              </div>
              <button
                onClick={() => toast.info('Key generation', { description: 'SSH key generation wizard coming soon' })}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#7A5CFF] text-white text-sm font-medium hover:bg-[#6B4FE0] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Key
              </button>
            </div>
            <div className="space-y-3">
              {sshKeys.map((key) => (
                <div key={key.id} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Key className="w-5 h-5 text-[#9CB0C7]" />
                      <div>
                        <p className="text-sm font-semibold text-white">{key.name}</p>
                        <code className="text-xs text-[#9CB0C7] font-mono">{key.fingerprint}</code>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${
                      key.status === 'active' ? 'text-[#3EF1C7] bg-[#3EF1C7]/10' :
                      key.status === 'expired' ? 'text-[#F55454] bg-[#F55454]/10' :
                      'text-[#9CB0C7] bg-[#9CB0C7]/10'
                    }`}>
                      {key.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#9CB0C7]">
                    <div className="flex items-center gap-4">
                      <span>Created: {key.created}</span>
                      <span>Last used: {key.last_used}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopyKey(key.fingerprint)}
                        className="p-1.5 rounded hover:bg-[#22304A] transition-colors text-[#9CB0C7] hover:text-white"
                        title="Copy fingerprint"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {key.status === 'active' && (
                        <button
                          onClick={() => handleRevokeKey(key.id)}
                          className="p-1.5 rounded hover:bg-[#F55454]/10 transition-colors text-[#9CB0C7] hover:text-[#F55454]"
                          title="Revoke key"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#7A5CFF]" />
                <h3 className="text-lg font-semibold text-white">Audit Log</h3>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 text-[#9CB0C7] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    placeholder="Search logs..."
                    className="pl-9 pr-4 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white text-sm placeholder-[#9CB0C7] focus:outline-none focus:border-[#7A5CFF] w-48"
                  />
                </div>
                <select
                  value={logFilter}
                  onChange={(e) => setLogFilter(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-white text-sm focus:outline-none focus:border-[#7A5CFF]"
                >
                  <option value="all">All Severities</option>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="space-y-3">
              {filteredLogs.map((entry) => {
                const Icon = severityIcon[entry.severity] || CheckCircle;
                return (
                  <div key={entry.id} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-4 flex items-start gap-3">
                    <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${severityColor[entry.severity]}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <code className="text-xs text-[#7A5CFF] font-mono">{entry.action}</code>
                          <span className="text-xs text-[#9CB0C7]">by {entry.user}</span>
                        </div>
                        <span className="text-xs text-[#9CB0C7] whitespace-nowrap">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-[#9CB0C7]">{entry.detail}</p>
                    </div>
                  </div>
                );
              })}
              {filteredLogs.length === 0 && (
                <div className="text-center py-8 text-[#9CB0C7] text-sm">No logs matching your filters</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'compliance' && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FileCheck className="w-5 h-5 text-[#3EF1C7]" />
              <h3 className="text-lg font-semibold text-white">Compliance Acknowledgement Tracker</h3>
            </div>
            <div className="space-y-3">
              {acknowledgements.map((ack) => (
                <div key={ack.id} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {ack.status === 'acknowledged' ? (
                        <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-[#FFC857]" />
                      )}
                      <div>
                        <p className="text-sm font-semibold text-white">{ack.policy}</p>
                        <div className="flex items-center gap-3 text-xs text-[#9CB0C7] mt-1">
                          <span>Required by: {ack.requiredBy}</span>
                          {ack.date && <span>Acknowledged: {ack.date}</span>}
                        </div>
                      </div>
                    </div>
                    {ack.status === 'pending' ? (
                      <button
                        onClick={() => handleAcknowledge(ack.id)}
                        className="px-4 py-2 rounded-lg bg-[#3EF1C7] text-[#0B1120] text-sm font-semibold hover:bg-[#35D9B2] transition-colors"
                      >
                        Acknowledge
                      </button>
                    ) : (
                      <span className="text-xs font-semibold text-[#3EF1C7] bg-[#3EF1C7]/10 px-3 py-1.5 rounded">
                        Acknowledged
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
